if (typeof PickupDatePicker !== 'function') {

  const MONTHS_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
  const WEEKDAYS_LONG = ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'];
  const WEEKDAY_KEYS = ['sun','mon','tue','wed','thu','fri','sat'];

  const pad = n => String(n).padStart(2, '0');
  const toIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fromIso = s => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };
  const formatDate = iso => {
    const d = fromIso(iso);
    if (!d) return '';
    return `${WEEKDAYS_LONG[d.getDay()]}, ${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  class PickupDatePicker extends HTMLElement {
    connectedCallback() {
      this.config = this._parseConfig();
      this.toggle = this.querySelector('[data-pickup-toggle]');
      this.body = this.querySelector('[data-pickup-body]');
      this.dateInput = this.querySelector('[data-pickup-date]');
      this.timeInput = this.querySelector('[data-pickup-time]');
      this.summaryBtn = this.querySelector('[data-pickup-summary-btn]');
      this.summaryText = this.querySelector('[data-pickup-summary]');
      this.errorEl = this.querySelector('[data-pickup-error]');

      this.modal = this.querySelector('[data-pickup-modal]');
      this.closeBtn = this.querySelector('[data-pickup-close]');
      this.cancelBtn = this.querySelector('[data-pickup-cancel]');
      this.applyBtn = this.querySelector('[data-pickup-apply]');

      this.calendar = this.querySelector('[data-pickup-calendar]');
      this.monthLabel = this.querySelector('[data-cal-month-label]');
      this.daysGrid = this.querySelector('[data-cal-days]');
      this.prevBtn = this.querySelector('[data-cal-prev]');
      this.nextBtn = this.querySelector('[data-cal-next]');
      this.slotsWrap = this.querySelector('[data-pickup-slots]');
      this.timeHeading = this.querySelector('[data-pickup-time-label]');

      if (!this.toggle || !this.body) return;

      this._computeBounds();

      // committed state = what's saved in cart attributes
      this.committedDate = this.dataset.initialDate || '';
      this.committedTime = this.dataset.initialTime || '';
      // pending state = what's being selected in the modal
      this.pendingDate = '';
      this.pendingTime = '';

      this.toggle.addEventListener('change', this._onToggle.bind(this));
      if (this.summaryBtn) this.summaryBtn.addEventListener('click', this._openModal.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this._cancelModal.bind(this));
      if (this.cancelBtn) this.cancelBtn.addEventListener('click', this._cancelModal.bind(this));
      if (this.applyBtn) this.applyBtn.addEventListener('click', this._applyModal.bind(this));
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._navMonth(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._navMonth(1));

      if (this.modal) {
        this.modal.addEventListener('cancel', e => {
          e.preventDefault();
          this._cancelModal();
        });
        this.modal.addEventListener('click', e => {
          if (e.target === this.modal) this._cancelModal();
        });
      }

      this._renderSummary();
    }

    _parseConfig() {
      try {
        return JSON.parse(this.dataset.config || '{}');
      } catch (e) {
        console.warn('[pickup-date-picker] failed to parse config', e);
        return { hours: {}, blockedDates: '', minLeadDays: 1, maxAdvanceDays: 30, slotStepMinutes: 30 };
      }
    }

    _computeBounds() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const min = new Date(today);
      min.setDate(min.getDate() + (Number(this.config.minLeadDays) || 0));
      const max = new Date(today);
      max.setDate(max.getDate() + (Number(this.config.maxAdvanceDays) || 30));
      this.minDate = min;
      this.maxDate = max;
    }

    _blockedSet() {
      if (this._blocked) return this._blocked;
      this._blocked = new Set();
      const raw = this.config.blockedDates || '';
      const matches = raw.matchAll(/\d{4}-\d{2}-\d{2}/g);
      for (const m of matches) {
        this._blocked.add(m[0]);
      }
      return this._blocked;
    }

    _hoursFor(date) {
      const key = WEEKDAY_KEYS[date.getDay()];
      const entry = (this.config.hours || {})[key];
      if (!entry || !entry.enabled || !entry.open || !entry.close) return null;
      return entry;
    }

    _dayAvailable(date) {
      if (date < this.minDate || date > this.maxDate) return false;
      if (!this._hoursFor(date)) return false;
      if (this._blockedSet().has(toIso(date))) return false;
      return true;
    }

    _onToggle(e) {
      if (e.target.checked) {
        this.body.hidden = false;
        this._renderSummary();
        if (!this.committedDate || !this.committedTime) {
          this._openModal();
        }
      } else {
        this.body.hidden = true;
        this.committedDate = '';
        this.committedTime = '';
        this.dateInput.value = '';
        this.timeInput.value = '';
        this._setError('');
        this._renderSummary();
        this._clearAttributes();
        this._emitChange();
      }
    }

    _isActive() {
      return !!(this.toggle && this.toggle.checked && this.committedDate && this.committedTime);
    }

    _emitChange() {
      document.dispatchEvent(new CustomEvent('pickup:change', {
        detail: { active: this._isActive() }
      }));
    }

    _openModal() {
      this.pendingDate = this.committedDate;
      this.pendingTime = this.committedTime;

      const focusIso = this.pendingDate || toIso(this.minDate);
      const focus = fromIso(focusIso) || this.minDate;
      this.viewYear = focus.getFullYear();
      this.viewMonth = focus.getMonth();

      this._renderCalendar();
      this._renderTimePanel();
      this._updateApplyButton();

      if (this.modal && typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else if (this.modal) {
        this.modal.setAttribute('open', '');
      }
    }

    _closeModalEl() {
      if (this.modal && typeof this.modal.close === 'function') {
        this.modal.close();
      } else if (this.modal) {
        this.modal.removeAttribute('open');
      }
    }

    _cancelModal() {
      this._closeModalEl();
      if (!this.committedDate || !this.committedTime) {
        this.toggle.checked = false;
        this.body.hidden = true;
      }
    }

    _applyModal() {
      if (!this.pendingDate || !this.pendingTime) return;
      this.committedDate = this.pendingDate;
      this.committedTime = this.pendingTime;
      this.dateInput.value = this.committedDate;
      this.timeInput.value = this.committedTime;
      this._setError('');
      this._renderSummary();
      this._saveAttributes();
      this._closeModalEl();
      this._emitChange();
    }

    _navMonth(delta) {
      const view = new Date(this.viewYear, this.viewMonth + delta, 1);
      const firstMin = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
      const firstMax = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
      if (view < firstMin) return;
      if (view > firstMax) return;
      this.viewYear = view.getFullYear();
      this.viewMonth = view.getMonth();
      this._renderCalendar();
    }

    _renderCalendar() {
      if (!this.daysGrid || !this.monthLabel) return;
      this.monthLabel.textContent = `${MONTHS_DE[this.viewMonth]} ${this.viewYear}`;

      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      let startOffset = firstOfMonth.getDay() - 1;
      if (startOffset < 0) startOffset = 6;

      const firstMin = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
      const firstMax = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
      if (this.prevBtn) this.prevBtn.disabled = new Date(this.viewYear, this.viewMonth, 1) <= firstMin;
      if (this.nextBtn) this.nextBtn.disabled = new Date(this.viewYear, this.viewMonth, 1) >= firstMax;

      const frag = document.createDocumentFragment();

      for (let i = 0; i < startOffset; i++) {
        const spacer = document.createElement('span');
        spacer.className = 'pickup-calendar__day pickup-calendar__day--spacer';
        spacer.setAttribute('aria-hidden', 'true');
        frag.appendChild(spacer);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(this.viewYear, this.viewMonth, d);
        const iso = toIso(date);
        const available = this._dayAvailable(date);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pickup-calendar__day';
        btn.textContent = String(d);
        btn.dataset.date = iso;
        btn.setAttribute('role', 'gridcell');

        if (!available) {
          btn.disabled = true;
          btn.classList.add('pickup-calendar__day--disabled');
        }
        if (iso === this.pendingDate) {
          btn.classList.add('pickup-calendar__day--selected');
          btn.setAttribute('aria-selected', 'true');
        }

        btn.addEventListener('click', () => this._onDayClick(iso));
        frag.appendChild(btn);
      }

      this.daysGrid.innerHTML = '';
      this.daysGrid.appendChild(frag);
    }

    _onDayClick(iso) {
      const date = fromIso(iso);
      if (!date || !this._dayAvailable(date)) return;

      this.pendingDate = iso;

      const hours = this._hoursFor(date);
      const slots = this._buildSlots(hours);
      if (!slots.includes(this.pendingTime)) {
        this.pendingTime = '';
      }

      this._renderCalendar();
      this._renderTimePanel();
      this._updateApplyButton();
    }

    _buildSlots(hours) {
      const step = Number(this.config.slotStepMinutes) || 30;
      const [oh, om] = hours.open.split(':').map(Number);
      const [ch, cm] = hours.close.split(':').map(Number);
      const start = oh * 60 + om;
      const end = ch * 60 + cm;
      const slots = [];
      for (let t = start; t <= end; t += step) {
        slots.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`);
      }
      return slots;
    }

    _renderTimePanel() {
      if (!this.slotsWrap || !this.timeHeading) return;

      if (!this.pendingDate) {
        this.timeHeading.textContent = 'Bitte zuerst ein Datum wählen.';
        this.slotsWrap.innerHTML = '';
        return;
      }

      const date = fromIso(this.pendingDate);
      const hours = date && this._hoursFor(date);
      if (!hours) {
        this.timeHeading.textContent = 'An diesem Tag ist keine Abholung möglich.';
        this.slotsWrap.innerHTML = '';
        return;
      }

      this.timeHeading.textContent = `Uhrzeit für ${formatDate(this.pendingDate)}`;

      const slots = this._buildSlots(hours);
      const frag = document.createDocumentFragment();
      slots.forEach(slot => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pickup-slots__slot';
        btn.textContent = slot;
        btn.dataset.time = slot;
        if (slot === this.pendingTime) {
          btn.classList.add('pickup-slots__slot--selected');
          btn.setAttribute('aria-pressed', 'true');
        }
        btn.addEventListener('click', () => this._onSlotClick(slot));
        frag.appendChild(btn);
      });
      this.slotsWrap.innerHTML = '';
      this.slotsWrap.appendChild(frag);
    }

    _onSlotClick(slot) {
      this.pendingTime = slot;
      this._renderTimePanel();
      this._updateApplyButton();
    }

    _updateApplyButton() {
      if (!this.applyBtn) return;
      this.applyBtn.disabled = !(this.pendingDate && this.pendingTime);
    }

    _renderSummary() {
      if (!this.summaryBtn || !this.summaryText) return;
      if (this.committedDate && this.committedTime) {
        this.summaryText.textContent = `${formatDate(this.committedDate)}, ${this.committedTime} Uhr`;
        this.summaryBtn.hidden = false;
      } else {
        this.summaryText.textContent = '';
        this.summaryBtn.hidden = true;
      }
    }

    _setError(msg) {
      if (!this.errorEl) return;
      if (msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    _saveAttributes() {
      this._postAttributes({ Abholdatum: this.committedDate, Abholuhrzeit: this.committedTime });
    }

    _clearAttributes() {
      this._postAttributes({ Abholdatum: '', Abholuhrzeit: '' });
    }

    _postAttributes(attrs) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      })
      .then(r => {
        if (!r.ok) throw new Error('cart update failed: ' + r.status);
        return r.json();
      })
      .catch(err => {
        console.warn('[pickup-date-picker] cart update failed', err);
        this._setError('Fehler beim Speichern. Bitte erneut versuchen.');
      });
    }
  }

  customElements.define('pickup-date-picker', PickupDatePicker);
}
